import { useTheme } from '@mui/material/styles';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  useMediaQuery,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import { useMutation } from '@tanstack/react-query';
import { AsyncAutocomplete } from '@pro4-erp/core-web';
import { getMessageFromError } from '@/core';
import { ClientProduct, WorkOrderContact } from '../types';
import { updateWOContact } from '../api/updateWOContact';
import { getClientProduct } from '../api/getClientProduct';

type ContactFormType = {
  name: string;
  mail: string;
  tel: string;
  mobile: string;
  role: string;
  product: ClientProduct;
};

type ContactModalProps = {
  title: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  contact?: WorkOrderContact;
  wo_id: string;
  refetchWo: () => void;
};

const ContactModal = ({
  title,
  isOpen,
  setIsOpen,
  contact,
  wo_id,
  refetchWo,
}: ContactModalProps) => {
  const theme = useTheme();
  const [showProductsPicker, setShowProductsPicker] = useState(contact?.role === 'AUD');
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();

  const form = useForm<ContactFormType>({
    mode: 'onTouched',
    defaultValues: {
      name: '',
      mail: '',
      tel: '',
      mobile: '',
      role: '',
      product: {
        name: '',
        id: '',
      },
    },
  });

  const { mutate } = useMutation({
    mutationFn: updateWOContact,
    onSuccess: () => {
      enqueueSnackbar(
        t('Proposal sent successfully.', {
          defaultValue: 'Predlog usprešno poslan.',
        }),
        { variant: 'success' }
      );
      refetchWo();
      setIsOpen(false);
    },
    onError: (error: Error) => {
      enqueueSnackbar(getMessageFromError(error), { variant: 'error' });
    },
  });

  useEffect(() => {
    if (!isOpen) {
      form.reset();
    } else {
      form.reset({
        name: contact?.name || '',
        mail: contact?.mail || '',
        tel: contact?.tel || '',
        mobile: contact?.mobile || '',
        role: contact?.role || '',
        product: contact?.product || { name: '', id: '' },
      });
      setShowProductsPicker(contact?.role === 'AUD');
    }
  }, [isOpen, form, contact]);

  const roleWatch = form.watch('role');

  useEffect(() => {
    if (roleWatch === 'AUD') {
      setShowProductsPicker(true);
    } else {
      setShowProductsPicker(false);
    }
  }, [roleWatch]);

  const baseSubmit = () => {
    mutate({
      data: {
        id: contact?.id || 'NEW',
        ...form.getValues(),
        action: 'UPDATE',
        wo_id,
      },
    });
  };

  const deleteAction = () => {
    mutate({
      data: {
        id: contact?.id || '',
        ...form.getValues(),
        action: 'DELETE',
        wo_id,
      },
    });
  };

  const handleClose = () => {
    form.reset();
    setIsOpen(false);
    setShowProductsPicker(false);
  };

  return (
    <Dialog
      fullScreen={fullScreen}
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="responsive-dialog-title"
      fullWidth
    >
      <DialogTitle id="responsive-dialog-title">{title}</DialogTitle>
      <DialogContent>
        <Box
          component="form"
          onSubmit={form.handleSubmit(baseSubmit)}
          noValidate
          sx={{ mt: '1rem' }}
        >
          <Controller
            control={form.control}
            name="name"
            rules={{
              required: t('Name is required.', { defaultValue: 'Name is required.' }).toString(),
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="name"
                fullWidth
                error={!!form.formState.errors.name}
                helperText={form.formState.errors.name?.message?.toString()}
              />
            )}
          />

          <Controller
            control={form.control}
            name="mail"
            render={({ field }) => (
              <TextField
                {...field}
                sx={{ mt: '1rem' }}
                label={t('Email', { defaultValue: 'Email' })}
                fullWidth
                error={!!form.formState.errors.mail}
                helperText={form.formState.errors.mail?.message?.toString()}
              />
            )}
          />

          <Controller
            control={form.control}
            name="tel"
            render={({ field }) => (
              <TextField
                {...field}
                sx={{ mt: '1rem' }}
                label={t('Phone', { defaultValue: 'Phone' })}
                fullWidth
                error={!!form.formState.errors.tel}
                helperText={form.formState.errors.tel?.message?.toString()}
              />
            )}
          />
          <Controller
            control={form.control}
            name="mobile"
            render={({ field }) => (
              <TextField
                {...field}
                sx={{ mt: '1rem' }}
                label={t('Mobile', { defaultValue: 'Mobile' })}
                fullWidth
                error={!!form.formState.errors.mobile}
                helperText={form.formState.errors.mobile?.message?.toString()}
              />
            )}
          />

          <Controller
            control={form.control}
            name="role"
            render={({ field }) => (
              <Box sx={{ display: 'flex', flexDirection: 'column', my: '0.5rem' }}>
                <InputLabel id="role-label">{t('Role', { defaultValue: 'Role' })}</InputLabel>
                <Select
                  {...field}
                  labelId="role-label"
                  id="role"
                  label={t('Role', { defaultValue: 'Role' })}
                  fullWidth
                  onChange={(e) => {
                    form.setValue('role', e.target.value);
                  }}
                >
                  <MenuItem value="DIR">{t('DIR', { defaultValue: 'DIR' })}</MenuItem>
                  <MenuItem value="AUD">{t('AUD', { defaultValue: 'AUD' })}</MenuItem>
                  <MenuItem value="FIN">{t('FIN', { defaultValue: 'FIN' })}</MenuItem>
                  <MenuItem value="PORTAL">{t('PORTAL', { defaultValue: 'PORTAL' })}</MenuItem>
                  <MenuItem value="OTH">{t('OTH', { defaultValue: 'OTH' })}</MenuItem>
                </Select>
              </Box>
            )}
          />
          {showProductsPicker && (
            <Box sx={{ mt: '1rem' }}>
              <AsyncAutocomplete<ClientProduct>
                id="product-list"
                label={t('Link to the product', { defaultValue: 'Link to the product' })}
                fullWidth
                getData={async (search) => {
                  const response = await getClientProduct(wo_id, {
                    page: 1,
                    pageSize: 100,
                    search,
                  });

                  return response.results;
                }}
                getOptionLabel={(option) => option.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                selectedOption={form.getValues('product')}
                setSelectedOption={(option) => {
                  form.setValue('product', option || { id: '', name: '' }, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                }}
              />
            </Box>
          )}

          <Button
            variant="contained"
            type="submit"
            fullWidth
            sx={{ mt: '1rem' }}
            disabled={!form.formState.isDirty}
          >
            {t('Submit', { defaultValue: 'Submit' })}
          </Button>

          {contact && (
            <Button
              variant="contained"
              type="reset"
              fullWidth
              sx={{ mt: '1rem' }}
              onClick={deleteAction}
            >
              {t('Delete', { defaultValue: 'Delete' })}
            </Button>
          )}

          <Button variant="outlined" fullWidth sx={{ mt: '1rem' }} onClick={handleClose}>
            {t('Cancel', { defaultValue: 'Cancel' })}
          </Button>

          {!!form.formState.errors.root && (
            <FormHelperText error>{form.formState.errors.root.message}</FormHelperText>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ContactModal;
